import React, { useState, useRef } from 'react';
import { Save, Eye, Send, Clock, X, Sparkles, Check, TestTube } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';
import VariableInsertionPanel from './VariableInsertionPanel';
import EmailTemplateSelector from './EmailTemplateSelector';
import RecipientSelector from './RecipientSelector';
import TemplateEditorModal from './TemplateEditorModal';

// ⭐ IMPORT: Blank email template constant
const BLANK_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Email Template</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4; margin: 0; padding: 0;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table cellpadding="0" cellspacing="0" border="0" style="max-width: 600px; width: 100%; background-color: #ffffff; margin: 0 auto;">
          
          <!-- Header Section -->
          <tr>
            <td style="background-color: #0066CC; color: #ffffff; padding: 40px 30px; text-align: center;">
              <img src="https://www.ellasmgt.com/logo.png" alt="Ella's Place" style="max-width: 180px; height: auto; margin: 0 auto 20px; display: block;">
              <h1 style="margin: 0; font-size: 28px; font-weight: 700; color: #ffffff;">Click to Edit Heading</h1>
            </td>
          </tr>
          
          <!-- Content Section -->
          <tr>
            <td style="padding: 40px 30px; color: #333333; line-height: 1.6;">
              <p style="margin: 15px 0; font-size: 16px;">Hi <strong>{{name}}</strong>,</p>
              <p style="margin: 15px 0; font-size: 16px;">Click this paragraph to edit your message. You can add personalization with variables like {{name}}, {{email}}, or {{loyaltyPoints}}.</p>
              <p style="margin: 15px 0; font-size: 16px;">Write your email content here. Keep it friendly, clear, and engaging!</p>
              
              <!-- CTA Button -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 25px 0;">
                <tr>
                  <td align="center">
                    <a href="https://wa.me/2349055444495" style="display: inline-block; background-color: #0066CC; color: #ffffff; text-decoration: none; padding: 15px 40px; border-radius: 5px; font-weight: 600; font-size: 16px;">Click to Edit Button</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Footer Section -->
          <tr>
            <td style="background: linear-gradient(to bottom, #f9f9f9 0%, #f5f5f5 100%); padding: 40px 30px; text-align: center; border-top: 3px solid #0066CC;">
              <div style="width: 80px; height: 2px; background-color: #0066CC; margin: 0 auto 25px; opacity: 0.3;"></div>
              <p style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #2c3e50; letter-spacing: 0.5px;">Ella's Place</p>
              <p style="margin: 0 0 20px 0; font-size: 11px; font-style: italic; color: #7f8c8d; letter-spacing: 0.3px;">Salon • Spa • Ladies Gym</p>
              <div style="margin: 20px 0; padding: 15px 0; border-top: 1px solid #e0e0e0; border-bottom: 1px solid #e0e0e0;">
                <p style="margin: 5px 0; font-size: 11px; color: #555555;">
                  <span style="display: inline-block; margin: 0 3px;">📞</span> 
                  <a href="tel:+2349055444495" style="color: #0066CC; text-decoration: none; font-weight: 500;">+234 905 544 4495</a>
                </p>
                <p style="margin: 5px 0; font-size: 11px; color: #555555;">
                  <span style="display: inline-block; margin: 0 3px;">📧</span> 
                  <a href="mailto:info@ellasplace.net" style="color: #0066CC; text-decoration: none; font-weight: 500;">info@ellasplace.net</a>
                </p>
                <p style="margin: 5px 0; font-size: 11px; color: #555555;">
                  <span style="display: inline-block; margin: 0 3px;">🌐</span> 
                  <a href="https://www.ellasplace.net" style="color: #0066CC; text-decoration: none; font-weight: 500;">www.ellasplace.net</a>
                </p>
              </div>
              <p style="margin: 20px 0 15px 0; font-size: 10px; font-style: italic; color: #95a5a6; line-height: 1.5;">
                Sent with love from Ella's Place ❤️
              </p>
              <p style="margin: 15px 0 0 0; font-size: 10px; color: #bdc3c7;">
                <a href="{{unsubscribeLink}}" style="color: #7f8c8d; text-decoration: underline; font-style: italic;">Unsubscribe</a> from marketing emails
              </p>
              <p style="margin: 15px 0 0 0; font-size: 9px; color: #bdc3c7; font-style: italic;">
                © ${new Date().getFullYear()} Ella's Place. All rights reserved.
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

const EnhancedEmailComposer = ({ 
  contacts = [], 
  settings = {}, 
  onSave, 
  onSend, 
  initialData = null 
}) => {
  // ═══════════════════════════════════════════════════════════════════
  // STATE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════
  const [showTemplateSelector, setShowTemplateSelector] = useState(!initialData);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [tempSelectedRecipients, setTempSelectedRecipients] = useState([]);
  
  // Template Editor State
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Send Test Email State
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailData, setTestEmailData] = useState({
    email: '',
    name: '',
    phone: '',
    businessUnit: 'Salon',
    loyaltyPoints: '1000'
  });
  const [sendingTest, setSendingTest] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  const [campaignData, setCampaignData] = useState({
    name: initialData?.name || '',
    subject: initialData?.subject || '',
    previewText: initialData?.previewText || '',
    fromName: initialData?.fromName || settings?.senderName || "Ella's Place",
    fromEmail: initialData?.fromEmail || settings?.senderEmail || 'info@ellasplace.net',
    replyTo: initialData?.replyTo || settings?.replyToEmail || 'info@ellasplace.net',
    recipientIds: initialData?.contactIds?.map(id => id.toString()) || initialData?.recipientIds || [],
    content: (typeof initialData?.content === 'object' ? initialData?.content?.body : initialData?.content) || '',
    scheduleType: initialData?.scheduleType || 'now',
    scheduledDate: initialData?.scheduledDate || '',
    scheduledTime: initialData?.scheduledTime || ''
  });

  const editorRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════
  // HANDLER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════

  // ⭐ UPDATED: Handle template selection with blank template support
  const handleSelectTemplate = (template) => {
    if (template) {
      // Regular template selected
      setCampaignData(prev => ({
        ...prev,
        subject: template.subject,
        previewText: template.previewText,
        content: template.htmlContent
      }));
      toast.success(`Template "${template.name}" loaded!`);
    } else {
      // ⭐ NEW: Load blank starter template instead of empty string
      setCampaignData(prev => ({
        ...prev,
        content: BLANK_EMAIL_TEMPLATE
      }));
      toast.info('✨ Starting with blank template - click elements to edit!');
    }
    setShowTemplateSelector(false);
  };

  // Handle template editing
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowTemplateEditor(true);
    setShowTemplateSelector(false);
  };

  // Handle template save from editor
  const handleSaveEditedTemplate = async (updatedTemplate) => {
    toast.success('Template updated successfully!');
    setShowTemplateEditor(false);
    setEditingTemplate(null);
  };

  // Handle recipient selection
  const handleRecipientSelection = (selectedIds) => {
    setTempSelectedRecipients(selectedIds);
  };

  // Confirm recipient selection
  const handleConfirmRecipients = () => {
    setCampaignData(prev => ({
      ...prev,
      recipientIds: tempSelectedRecipients
    }));
    setShowRecipientSelector(false);
    toast.success(`${tempSelectedRecipients.length} recipient(s) selected!`);
  };

  // Open recipient selector
  const handleOpenRecipientSelector = () => {
    setTempSelectedRecipients(campaignData.recipientIds);
    setShowRecipientSelector(true);
  };

  // Handle variable insertion
  const handleInsertVariable = (insertFn) => {
    const variable = prompt('Choose variable:\n1. {{name}}\n2. {{email}}\n3. {{phone}}\n4. {{businessUnit}}\n5. {{loyaltyPoints}}\n6. {{unsubscribeLink}}');
    const variableMap = {
      '1': '{{name}}',
      '2': '{{email}}',
      '3': '{{phone}}',
      '4': '{{businessUnit}}',
      '5': '{{loyaltyPoints}}',
      '6': '{{unsubscribeLink}}'
    };
    if (variableMap[variable]) {
      insertFn(variableMap[variable]);
    }
  };

  // Handle inserting variable from panel
  const handleInsertVariableFromPanel = (variable) => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection() || { index: editor.getLength() };
      editor.insertText(range.index, variable);
      editor.setSelection(range.index + variable.length);
      toast.success('Variable inserted!');
    } else {
      toast.error('Editor not ready, please try again');
    }
  };

  // Handle logo insertion
  const handleInsertLogo = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection() || { index: editor.getLength() };
      editor.insertEmbed(range.index, 'image', 'https://www.ellasmgt.com/logo.png');
      editor.insertText(range.index + 1, '\n');
      toast.success('Logo inserted!');
    }
  };

  // Handle WhatsApp link insertion
  const handleInsertWhatsApp = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection() || { index: editor.getLength() };
      const whatsappText = '💬 Chat on WhatsApp';
      const whatsappUrl = 'https://wa.me/2349055444495';
      editor.insertText(range.index, whatsappText, 'link', whatsappUrl);
      toast.success('WhatsApp link inserted!');
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle Send Test Email
  const handleSendTestEmail = async () => {
    // Validation
    if (!testEmailData.email) {
      toast.error('Please enter a test email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmailData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!campaignData.subject) {
      toast.error('Please enter email subject first');
      return;
    }
    
    if (!campaignData.content) {
      toast.error('Please create email content first');
      return;
    }
    
    setSendingTest(true);
    
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/email-campaigns/send-test`,
        {
          toEmail: testEmailData.email,
          toName: testEmailData.name || 'Test User',
          subject: campaignData.subject,
          content: campaignData.content,
          fromName: campaignData.fromName,
          fromEmail: campaignData.fromEmail,
          replyTo: campaignData.replyTo,
          testPhone: testEmailData.phone,
          testBusinessUnit: testEmailData.businessUnit,
          testLoyaltyPoints: testEmailData.loyaltyPoints
        }
      );
      
      toast.success(`Test email sent to ${testEmailData.email}! Check your inbox.`);
      setShowTestEmailModal(false);
      
      // Reset test data for next time
      setTestEmailData({
        email: '',
        name: '',
        phone: '',
        businessUnit: 'Salon',
        loyaltyPoints: '1000'
      });
      
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.error || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Validate campaign
  const validateCampaign = () => {
    if (!campaignData.subject) {
      toast.error('Subject is required');
      return false;
    }
    if (campaignData.recipientIds.length === 0) {
      toast.error('Please select at least one recipient');
      return false;
    }
    if (!campaignData.content) {
      toast.error('Email content is required');
      return false;
    }
    return true;
  };

  // Handle save draft
const handleSaveDraft = async () => {
  if (!campaignData.subject) {
    toast.error('Subject is required to save draft');
    return;
  }
  
  setIsSavingDraft(true);
  try {
    const draftData = {
      ...campaignData,
      status: 'draft'
    };
    
    if (onSave) {
      await onSave(draftData);
    }
  } catch (error) {
    console.error('Error saving draft:', error);
    toast.error('Failed to save draft');
  } finally {
    setIsSavingDraft(false);
  }
};

  // Handle send now
const handleSendNow = async () => {
  if (!validateCampaign()) return;
  
  if (!window.confirm(`Send email to ${campaignData.recipientIds.length} recipient(s) now?`)) {
    return;
  }
  
  setIsSending(true);
  try {
    const sendData = {
      ...campaignData,
      status: 'sent',
      scheduleType: 'now'
    };
    
    if (onSend) {
      await onSend(sendData);
    }
  } catch (error) {
    console.error('Error sending campaign:', error);
    toast.error('Failed to send campaign');
  } finally {
    setIsSending(false);
  }
};

  // Handle schedule
const handleSchedule = async () => {
  if (!validateCampaign()) return;
  
  if (!campaignData.scheduledDate || !campaignData.scheduledTime) {
    toast.error('Please select a date and time for scheduling');
    return;
  }
  
  setIsSending(true);
  try {
    const scheduleData = {
      ...campaignData,
      status: 'scheduled',
      scheduleType: 'scheduled'
    };
    
    if (onSave) {
      await onSave(scheduleData);
    }
  } catch (error) {
    console.error('Error scheduling campaign:', error);
    toast.error('Failed to schedule campaign');
  } finally {
    setIsSending(false);
  }
};

  // Render preview with sample data
  const renderPreview = () => {
    let previewContent = campaignData.content;
    
    previewContent = previewContent.replace(/{{name}}/g, 'Jane Doe');
    previewContent = previewContent.replace(/{{email}}/g, 'jane@example.com');
    previewContent = previewContent.replace(/{{phone}}/g, '+234 801 234 5678');
    previewContent = previewContent.replace(/{{businessUnit}}/g, 'Salon');
    previewContent = previewContent.replace(/{{loyaltyPoints}}/g, '1250');
    previewContent = previewContent.replace(/{{unsubscribeLink}}/g, '#unsubscribe');

    return previewContent;
  };

  // ╔═══════════════════════════════════════════════════════════════════════════╗
  // RENDER COMPONENT
  // ╚═══════════════════════════════════════════════════════════════════════════╝

  return (
    <div className="enhanced-email-composer">
      {/* 🔄 LOADING OVERLAY */}
      {isSending && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
            <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {campaignData.scheduleType === 'scheduled' ? 'Scheduling Campaign...' : 'Sending Email Campaign...'}
            </h3>
            <p className="text-gray-600 mb-4">
              {campaignData.scheduleType === 'scheduled' 
                ? `Scheduling for ${campaignData.scheduledDate} at ${campaignData.scheduledTime}`
                : `Sending to ${campaignData.recipientIds.length} recipient(s)`
              }
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
              <span>Please wait, do not close this page...</span>
            </div>
          </div>
        </div>
      )}

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Choose Email Template</h2>
              <button
                onClick={() => setShowTemplateSelector(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <EmailTemplateSelector 
                onSelectTemplate={handleSelectTemplate}
                onEditTemplate={handleEditTemplate}
              />
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <TemplateEditorModal
          template={editingTemplate}
          onClose={() => {
            setShowTemplateEditor(false);
            setEditingTemplate(null);
          }}
          onSave={handleSaveEditedTemplate}
        />
      )}

      {/* Recipient Selector Modal */}
      {showRecipientSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">Select Recipients</h2>
              <button
                onClick={() => setShowRecipientSelector(false)}
                className="text-gray-500 hover:text-gray-700 transition"
                title="Close"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <RecipientSelector
                contacts={contacts}
                onSelectionChange={handleRecipientSelection}
                preSelected={tempSelectedRecipients}
              />
            </div>
            
            <div className="p-4 border-t flex justify-between items-center flex-shrink-0 bg-gray-50">
              <div className="text-sm text-gray-600">
                {tempSelectedRecipients.length} recipient(s) selected
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRecipientSelector(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRecipients}
                  disabled={tempSelectedRecipients.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Email Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
              <div className="bg-white rounded-lg shadow-lg max-w-2xl mx-auto">
                <div className="p-4 border-b bg-gray-50">
                  <div className="text-sm text-gray-600 mb-1">
                    <strong>From:</strong> {campaignData.fromName} &lt;{campaignData.fromEmail}&gt;
                  </div>
                  <div className="text-sm text-gray-600 mb-1">
                    <strong>Subject:</strong> {campaignData.subject}
                  </div>
                  {campaignData.previewText && (
                    <div className="text-xs text-gray-500 italic mt-2">
                      {campaignData.previewText}
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <iframe
                    srcDoc={renderPreview()}
                    className="w-full border-none"
                    style={{ minHeight: '400px', height: 'auto' }}
                    title="Email Preview"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-500 to-yellow-500">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <TestTube size={20} />
                Send Test Email
              </h3>
              <button
                onClick={() => setShowTestEmailModal(false)}
                className="text-white hover:text-gray-200"
                disabled={sendingTest}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Send a test email to verify formatting and content before sending to all recipients.
              </p>
              
              <div className="space-y-4">
                {/* Email Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Email Address *
                  </label>
                  <input
                    type="email"
                    value={testEmailData.email}
                    onChange={(e) => setTestEmailData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="test@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    disabled={sendingTest}
                  />
                </div>
                
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    value={testEmailData.name}
                    onChange={(e) => setTestEmailData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    disabled={sendingTest}
                  />
                  <p className="text-xs text-gray-500 mt-1">Will replace {'{{name}}'} in email</p>
                </div>
                
                {/* Optional Fields */}
                <details className="border border-gray-200 rounded-md">
                  <summary className="px-3 py-2 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Optional Test Data (for variables)
                  </summary>
                  <div className="p-3 space-y-3 border-t">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={testEmailData.phone}
                        onChange={(e) => setTestEmailData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+234 801 234 5678"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                        disabled={sendingTest}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Business Unit
                      </label>
                      <select
                        value={testEmailData.businessUnit}
                        onChange={(e) => setTestEmailData(prev => ({ ...prev, businessUnit: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                        disabled={sendingTest}
                      >
                        <option value="Salon">Salon</option>
                        <option value="Spa">Spa</option>
                        <option value="Gym">Gym</option>
                        <option value="General">General</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Loyalty Points
                      </label>
                      <input
                        type="text"
                        value={testEmailData.loyaltyPoints}
                        onChange={(e) => setTestEmailData(prev => ({ ...prev, loyaltyPoints: e.target.value }))}
                        placeholder="1000"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                        disabled={sendingTest}
                      />
                    </div>
                  </div>
                </details>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowTestEmailModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                disabled={sendingTest}
              >
                Cancel
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTest || !testEmailData.email}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-md hover:shadow-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingTest ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <TestTube size={16} />
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Composer Interface */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">Email Campaign Composer</h2>
              <p className="text-white/90 text-sm mt-1">
                Create stunning emails in minutes • {campaignData.recipientIds.length} recipients selected
              </p>
            </div>
          </div>
        </div>

        {/* Campaign Details Form */}
        <div className="p-6 bg-gray-50 border-b">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name *
              </label>
              <input
                type="text"
                value={campaignData.name}
                onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Promotion 2025"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Subject *
              </label>
              <input
                type="text"
                value={campaignData.subject}
                onChange={(e) => setCampaignData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="e.g., Exclusive Summer Offer Just For You!"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preview Text
              </label>
              <input
                type="text"
                value={campaignData.previewText}
                onChange={(e) => setCampaignData(prev => ({ ...prev, previewText: e.target.value }))}
                placeholder="Short preview text shown in inbox"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Name
              </label>
              <input
                type="text"
                value={campaignData.fromName}
                onChange={(e) => setCampaignData(prev => ({ ...prev, fromName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Email
              </label>
              <input
                type="email"
                value={campaignData.fromEmail}
                onChange={(e) => setCampaignData(prev => ({ ...prev, fromEmail: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reply-To Email
              </label>
              <input
                type="email"
                value={campaignData.replyTo}
                onChange={(e) => setCampaignData(prev => ({ ...prev, replyTo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => setShowTemplateSelector(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition text-sm flex items-center gap-2"
            >
              📧 Load Template
            </button>
            <button
              onClick={handleOpenRecipientSelector}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm flex items-center gap-2"
            >
              👥 Select Recipients ({campaignData.recipientIds.length})
            </button>
            <button
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition text-sm flex items-center gap-2"
            >
              <Eye size={16} />
              Preview
            </button>
          </div>
        </div>

        {/* Editor and Variable Panel */}
        <div className="grid grid-cols-[1fr_300px] gap-0">
          <div className="p-6 border-r">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Content</h3>
            <RichTextEditor
              ref={editorRef}
              value={campaignData.content}
              onChange={(content) => setCampaignData(prev => ({ ...prev, content }))}
              onImageUpload={handleImageUpload}
              onInsertVariable={handleInsertVariable}
              onInsertLogo={handleInsertLogo}
            />
          </div>

          <div className="p-6 bg-gray-50 overflow-y-auto" style={{ maxHeight: '800px' }}>
            <VariableInsertionPanel
              onInsertVariable={handleInsertVariableFromPanel}
              onInsertLogo={handleInsertLogo}
              onInsertWhatsApp={handleInsertWhatsApp}
            />
          </div>
        </div>

        {/* Schedule Options */}
        {campaignData.scheduleType === 'scheduled' && (
          <div className="p-6 bg-yellow-50 border-t border-yellow-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock size={20} />
              Schedule Campaign
            </h3>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={campaignData.scheduledDate}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={campaignData.scheduledTime}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
<div className="p-6 bg-gray-50 border-t">
  <div className={`flex justify-between items-center ${isSending ? 'pointer-events-none opacity-50' : ''}`}>
    <div className="flex gap-3">
      {/* TEMPORARILY DISABLED - Save Draft
      <button
        onClick={handleSaveDraft}
        disabled={isSending || isSavingDraft}
        className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSavingDraft ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Saving...
          </>
        ) : (
          <>
            <Save size={18} />
            Save Draft
          </>
        )}
      </button>
      */}
      
      <button
        onClick={() => setCampaignData(prev => ({ 
          ...prev, 
          scheduleType: prev.scheduleType === 'scheduled' ? 'now' : 'scheduled' 
        }))}
        disabled={isSending}
        className="px-6 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Clock size={18} />
        {campaignData.scheduleType === 'scheduled' ? 'Send Now Instead' : 'Schedule'}
      </button>
      <button
        onClick={() => setShowTestEmailModal(true)}
        disabled={isSending}
        className="px-6 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-md hover:shadow-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Send test email to verify formatting"
      >
        <TestTube size={18} />
        Send Test
      </button>
    </div>
    
    <button
      onClick={campaignData.scheduleType === 'scheduled' ? handleSchedule : handleSendNow}
      disabled={isSending || isSavingDraft}
      className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-md hover:shadow-lg transition text-lg font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isSending ? (
        <>
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          {campaignData.scheduleType === 'scheduled' ? 'Scheduling...' : 'Sending...'}
        </>
      ) : campaignData.scheduleType === 'scheduled' ? (
        <>
          <Clock size={20} />
          Schedule Campaign
        </>
      ) : (
        <>
          <Send size={20} />
          Send Now
        </>
      )}
    </button>
  </div>
          
          {/* Help Text */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>💡 Tip:</strong> Use the "Send Test" button to send a test email to yourself before sending to all recipients. 
              Variables like {'{{name}}'} will be replaced with test data you provide!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedEmailComposer;